import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Link from '../../../components/ui/Link';

const QuickPreviewModal = ({ 
  model, 
  isOpen, 
  onClose, 
  onAddToCart, 
  onAddToWishlist,
  onAddToCollection,
  onRemoveFromCollection,
  isInCollection = false,
  isAuthenticated, // ✅ NEW: Receive isAuthenticated prop
  onAuthRequired, // ✅ NEW: Receive onAuthRequired prop
  isGlobalCatalog = false
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setCurrentImageIndex(0);
      // Simulate 3D model loading
      const timer = setTimeout(() => setIsLoading(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, model]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !model) return null;

  // Build images array: primary thumbnail + additional images from database
  const images = [
    model?.thumbnailPath,  // Primary image from thumbnail_path
    ...(model?.imageUrls || [])  // Additional images from image_urls JSONB array
  ]?.filter(Boolean);  // Remove any null/undefined entries

  // Build corresponding alt text array
  const imageAlts = [
    `${model?.name} - Primary product image`,  // Alt for primary image
    ...Array(model?.imageUrls?.length || 0)?.fill(null)?.map((_, index) => 
      `${model?.name} - Additional view ${index + 1}`
    )
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images?.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images?.length) % images?.length);
  };

  
  
                const formatSeoPath = (name, categoryName) => {
    if (!name) return '';
    const safeCat = (categoryName || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    let prefix = `/product`;
    if (['automotive', 'motorcycles'].includes(safeCat)) prefix = '/vehicle';
    
    let processedName = name.toLowerCase();
    let year = '';
    if (prefix === '/vehicle') {
      const yearMatch = processedName.match(/\b(19\d{2}|20\d{2})\b/);
      if (yearMatch) {
        year = `/${yearMatch[1]}`;
        processedName = processedName.replace(yearMatch[0], '').trim();
      }
    }
    
    const pathSegments = processedName.replace(/[^a-z0-9\s.-]/g, '').trim().split(/\s+/).filter(Boolean).join('/');
    return `${prefix}${year}/${pathSegments}`;
  };

  const handleView3DModel = () => {
    if (model?.name) {
      navigate(`${formatSeoPath(model.name, model.categoryName || model.category?.name)}/parts-diagram?id=${model.id}`);
    } else {
      navigate(`/3d-model-viewer?id=${model?.id}&name=${encodeURIComponent(model?.name || '')}&part_count=${model?.componentCount || 0}`);
    }
    onClose();
  };

  const handleViewRatings = () => {
    if (!isAuthenticated) {
      onAuthRequired('Please log in to view ratings and reviews.');
      return;
    }
    navigate(`/product-reviews-ratings?productId=${model?.id}`);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'low': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'high': return 'text-error bg-error/10';
      case 'ultra': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const handleAddToCartClick = async () => {
    if (isGlobalCatalog) {
      onAddToCart(model);
      onClose();
      return;
    }

    if (!isAuthenticated) {
      onAuthRequired('Please log in to add items to cart.');
      return;
    }
    try {
      await onAddToCart(model?.id);
      // Only close modal after successful cart addition
      onClose();
    } catch (error) {
      console.error('Failed to add to cart from modal:', error);
      // Keep modal open on error so user can see the notification
    }
  };

  const handleAddToWishlistClick = () => {
    if (!isAuthenticated) {
      onAuthRequired('Please log in to add items to wishlist.');
      return;
    }
    onAddToWishlist(model?.id);
  };

  const handleAddToCollectionClick = () => {
    if (!isAuthenticated) {
      onAuthRequired('Please log in to add to your collection.');
      return;
    }
    if (isInCollection) {
      onRemoveFromCollection(model?.id);
    } else {
      onAddToCollection(model?.id);
    }
  };

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) return 'Free';
    return `$${Number(price)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal Content */}
      <div
        className="bg-card rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden relative transform transition-all duration-300"
        style={{ transform: isOpen ? 'scale(1)' : 'scale(0.9)' }}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-foreground truncate">
              {model?.name}
            </h2>
            {/* Creator hidden for now - will be reimplemented later
            <p className="text-sm text-muted-foreground mt-1">
              by {model?.creator}
            </p>
            */}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="flex-shrink-0 ml-4"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Image/3D Viewer */}
          <div className="flex-1 relative bg-muted flex flex-col">
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading 3D preview...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="relative flex-1 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                  <Image
                    src={images?.[currentImageIndex]}
                    alt={imageAlts?.[currentImageIndex]}
                    className="w-full h-full object-contain p-4"
                  />

                  {/* View 3D Model Button - Top Right */}
                  <button
                    onClick={handleView3DModel}
                    className="absolute top-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 shadow-lg"
                  >
                    <Icon name="Box" size={18} />
                    <span className="text-sm font-medium">View 3D Model</span>
                  </button>

                  {/* Image Navigation Arrows */}
                  {images?.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/75 transition-colors"
                      >
                        <Icon name="ChevronLeft" size={20} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/75 transition-colors"
                      >
                        <Icon name="ChevronRight" size={20} />
                      </button>
                    </>
                  )}

                  {/* View Type Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-black/70 text-white text-xs rounded-full backdrop-blur-sm">
                      {currentImageIndex === 0 ? 'Preview' : 
                       currentImageIndex === 1 ? 'Wireframe' :
                       currentImageIndex === 2 ? 'Texture' : 'Animation'}
                    </span>
                  </div>
                </div>

                {/* Horizontal Thumbnail Slider */}
                {images?.length > 1 && (
                  <div className="border-t border-border bg-card/50 backdrop-blur-sm">
                    <div className="flex overflow-x-auto space-x-2 p-3 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                      {images?.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentImageIndex 
                              ? 'border-primary shadow-lg scale-105' 
                              : 'border-border hover:border-primary/50 opacity-70 hover:opacity-100'
                          }`}
                        >
                          <Image
                            src={image}
                            alt={imageAlts?.[index]}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Model Details */}
          <div className="space-y-4">
            {/* Price and Actions */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                {/* Price hidden for now - will be reimplemented later
                <div className="flex-shrink-0">
                  <div className="text-2xl font-bold text-foreground">
                    {formatPrice(model?.price)}
                  </div>
                  {model?.originalPrice && model?.originalPrice > model?.price && (
                    <div className="text-sm text-muted-foreground line-through">
                      {formatPrice(model?.originalPrice)}
                    </div>
                  )}
                </div>
                */}
                <button
                  onClick={handleViewRatings}
                  className={`flex items-center gap-2 hover:bg-muted/50 px-3 py-2 rounded-md transition-colors cursor-pointer min-w-[120px] flex-shrink-0 ${!isAuthenticated ? 'opacity-50 cursor-pointer' : ''}`} // Added cursor-pointer
                  title={!isAuthenticated ? 'Log in to view ratings and reviews' : 'View all ratings and reviews'}
                >
                  <Icon name="Star" size={18} className="text-warning fill-current flex-shrink-0" />
                  <span className="text-base font-semibold text-foreground whitespace-nowrap">
                    {model?.averageRating?.toFixed(1) || '0.0'}
                  </span>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    ({model?.totalRatings || 0})
                  </span>
                </button>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={handleAddToCartClick}
                  className={`w-full ${!isGlobalCatalog && !isAuthenticated ? 'opacity-50 cursor-pointer' : ''}`}
                  iconName={isGlobalCatalog ? (model?.activeSellerCount > 0 ? "Store" : "Box") : "ShoppingCart"}
                  iconPosition="left"
                >
                  {isGlobalCatalog ? ((model?.activeSellerCount > 0 || model?.hasManufacturing || model?.sellProductOnly || model?.sell_product_only) ? 'Add to Cart' : 'Choose Parts') : 'Add to Cart'}
                </Button>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={handleAddToWishlistClick}
                    iconName="Heart"
                    iconPosition="left"
                    className={`${!isAuthenticated ? 'opacity-50 cursor-pointer' : ''}`} // Added cursor-pointer
                  >
                    Wishlist
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleAddToCollectionClick}
                    iconName={isInCollection ? 'FolderCheck' : 'FolderPlus'}
                    iconPosition="left"
                    className={`${isInCollection ? 'border-primary text-primary' : ''} ${!isAuthenticated ? 'opacity-50 cursor-pointer' : ''}`} // Added cursor-pointer
                  >
                    {isInCollection ? 'In Collection' : 'Add to Collection'}
                  </Button>
                </div>
                <Link to={model?.name ? `${formatSeoPath(model.name, model.categoryName || model.category?.name)}/parts-diagram?id=${model.id}` : `/3d-model-viewer?id=${model?.id}&name=${encodeURIComponent(model?.name || '')}&part_count=${model?.componentCount || 0}`}>
                  <Button
                    variant="secondary"
                    className="w-full"
                    iconName="Maximize2"
                    iconPosition="left"
                  >
                    View Full Details
                  </Button>
                </Link>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-foreground mb-2">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {model?.description}
              </p>
            </div>

            {/* Tags */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-foreground mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(model?.complexity)}`}>
                  {model?.complexity}
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium text-primary bg-primary/10">
                  {model?.license}
                </span>
                {model?.animated && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium text-accent bg-accent/10">
                    Animated
                  </span>
                )}
                <span className="px-2 py-1 rounded-full text-xs font-medium text-muted-foreground bg-muted">
                  {model?.category}
                </span>
              </div>
            </div>

            {/* Technical Specs */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-foreground mb-3">Technical Specifications</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">File Size:</span>
                  <span className="font-medium">{formatFileSize(model?.fileSize)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Polygons:</span>
                  <span className="font-medium">{model?.polygons?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{(model?.polygons * 1.5)?.toFixed(0)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Format:</span>
                  <span className="font-medium">GLTF 2.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Textures:</span>
                  <span className="font-medium">4K PBR</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Animation:</span>
                  <span className="font-medium">{model?.animated ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-lg font-semibold text-foreground">
                    {model?.downloads?.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Downloads</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-foreground">
                    {model?.views?.toLocaleString() || '12.5K'}
                  </div>
                  <div className="text-xs text-muted-foreground">Views</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickPreviewModal;